// storm-allow-pin-palette
// PIN-DATA: this file emits per-feature `active_color` injected into the
// MapLibre symbol layer's `icon-image` expression which keys into the D-69
// LOCKED sprite atlas (green/yellow/red/grey). The `// storm-allow-pin-palette`
// directive above silences the chrome ESLint rule
// (storm/no-legacy-palette-in-chrome) for this file. Locked-grep gate honored
// via `LOCKED:OVERRIDE:` (start-of-line + colon-delimited; matches the
// gate's regex `^LOCKED:OVERRIDE:` at chrome-pin-locked-grep.sh:31) on
// the commit that introduced this comment (Phase 9 Wave 2 pre-swap).
/**
 * Client-side dimension recolor logic for the map's `assets` GeoJSON source.
 *
 * The server's GeoJSON endpoint embeds 5 derived `*_color` properties on
 * every feature (D-72 + Plan 01). The active dimension determines which one
 * the symbol layer's `icon-image` expression reads from. The expression uses
 * a synthetic `active_color` property that we inject client-side via this
 * helper — the server never serializes `active_color`.
 *
 * Recolor flow per D-71 + D-87:
 *   1. User clicks a different dimension pill.
 *   2. Component calls `withActiveColor(geoJson, newDim)`.
 *   3. `map.getSource('assets').setData(updatedFc)` — single GL call.
 *   4. MapLibre re-evaluates the data-driven `icon-image` expression
 *      `["concat", ["get","shape"], "-", ["get","active_color"]]` for every
 *      feature → the sprite atlas key swaps → all pins recolor.
 *
 * Latency budget: ≤ 200ms perceived recolor (D-87). The .map() over 2,500
 * features is ~5ms on a desktop browser; setData triggers a single tile
 * pyramid invalidation; total well within budget.
 */
import type { AssetFeature, AssetFeatureCollection, AssetFeatureProperties } from "../../api/assets";

export type Dimension =
  | "cleaning"
  | "coating"
  | "cover_level"
  | "pipe_blockage"
  | "structure";

export const DIMENSIONS: readonly Dimension[] = [
  "cleaning",
  "coating",
  "cover_level",
  "pipe_blockage",
  "structure",
] as const;

const DIM_TO_COLOR_KEY: Record<Dimension, keyof AssetFeatureProperties> = {
  cleaning: "cleaning_color",
  coating: "coating_color",
  cover_level: "cover_level_color",
  pipe_blockage: "pipe_blocked_color",
  structure: "structure_color",
};

export function colorForDimension(props: AssetFeatureProperties, dim: Dimension): string {
  return props[DIM_TO_COLOR_KEY[dim]] as string;
}

/** Re-emit a FeatureCollection where every feature carries `active_color`
 *  set to the value of the per-dimension color property. The original
 *  per-dimension colors stay on every feature so the panel can render the
 *  full DimensionChip row independent of the active filter.
 */
export function withActiveColor(
  fc: AssetFeatureCollection,
  dim: Dimension,
): AssetFeatureCollection {
  const colorKey = DIM_TO_COLOR_KEY[dim];
  return {
    type: "FeatureCollection",
    features: fc.features.map((f: AssetFeature) => ({
      ...f,
      properties: {
        ...f.properties,
        active_color: f.properties[colorKey],
      },
    })) as AssetFeature[],
  };
}
